import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import {
  CalendarDays,
  Clock,
  Video,
  Phone,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingActions from "../components/FloatingActions";
import { Calendar } from "../components/ui/calendar";
import { api, SERVICES } from "../lib/data";

const TIME_SLOTS = [
  "09:30 AM", "10:30 AM", "11:30 AM", "12:30 PM",
  "02:30 PM", "03:30 PM", "04:30 PM", "05:30 PM", "06:30 PM",
];

const MEETING_TYPES = [
  { id: "Google Meet", label: "Google Meet", icon: Video, desc: "Video call link" },
  { id: "Phone Call", label: "Phone Call", icon: Phone, desc: "We'll call you" },
];

export default function Booking() {
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [meetingType, setMeetingType] = useState("Google Meet");
  const [booked, setBooked] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", service: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!date) return;
    const iso = format(date, "yyyy-MM-dd");
    setTime("");
    api
      .get(`/availability?date=${iso}`)
      .then((res) => setBooked(res.data.booked_slots || []))
      .catch(() => setBooked([]));
  }, [date]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Please pick a date.");
    if (!time) return toast.error("Please pick a time slot.");
    if (!form.name || !form.email || !form.whatsapp || !form.service) {
      return toast.error("Please complete all required fields.");
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        date: format(date, "yyyy-MM-dd"),
        time,
        meeting_type: meetingType,
      };
      const res = await api.post("/bookings", payload);
      setDone(res.data);
      toast.success("Appointment confirmed! Check your details below.");
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("That slot was just taken. Please pick another time.");
        const iso = format(date, "yyyy-MM-dd");
        api.get(`/availability?date=${iso}`).then((r) => setBooked(r.data.booked_slots || []));
      } else {
        toast.error("Booking failed. Please try WhatsApp or call us.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="booking-page" className="min-h-screen">
      <Navbar />
      <FloatingActions />

      <section className="relative overflow-hidden pt-36 pb-16">
        <div className="pointer-events-none absolute left-1/2 top-10 h-[400px] w-[600px] -translate-x-1/2 rounded-full bg-white/15 blur-[140px]" />
        <div className="relative mx-auto max-w-[90rem] px-6 md:px-12">
          <Link
            to="/"
            data-testid="booking-back"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/75 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} /> Back home
          </Link>
          <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-white">
            / Book a growth diagnosis
          </p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[0.95] tracking-tighter text-white md:text-7xl">
            30 minutes. Zero pitch. <span className="font-serif-emph font-normal">Three real fixes.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-white/70 leading-relaxed">
            Pick a time. We’ll open your funnel on screen, point out three leaks
            you can plug this week, and only mention hiring us at the end — if
            it’s obvious we can help.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[90rem] px-6 pb-32 md:px-12">
        {done ? (
          <Confirmation done={done} onReset={() => { setDone(null); setDate(undefined); setTime(""); setForm({ name: "", email: "", whatsapp: "", service: "", notes: "" }); }} />
        ) : (
          <div className="grid gap-6 lg:grid-cols-12">
            {/* Calendar + slots */}
            <div className="lg:col-span-7">
              <div className="glass rounded-3xl p-6 md:p-8" data-testid="calendar-card">
                <div className="mb-6 flex items-center gap-2 text-white">
                  <CalendarDays size={20} className="text-white" />
                  <h3 className="font-display text-xl font-bold tracking-tight">Select a date</h3>
                </div>
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return d < today || d.getDay() === 0;
                    }}
                    className="rounded-2xl text-white"
                  />
                </div>

                <div className="mt-8">
                  <div className="mb-4 flex items-center gap-2 text-white">
                    <Clock size={20} className="text-white" />
                    <h3 className="font-display text-xl font-bold tracking-tight">Select a time</h3>
                  </div>
                  {!date ? (
                    <p className="text-white/65">Choose a date first to see available slots.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                      {TIME_SLOTS.map((slot) => {
                        const taken = booked.includes(slot);
                        return (
                          <button
                            key={slot}
                            disabled={taken}
                            onClick={() => setTime(slot)}
                            data-testid={`slot-${slot.replace(/[:\s]/g, "")}`}
                            className={`rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${
                              time === slot
                                ? "border-white bg-white text-black"
                                : taken
                                ? "cursor-not-allowed border-white/5 bg-white/[0.02] text-white/70 line-through"
                                : "border-white/10 text-white/85 hover:border-white/60"
                            }`}
                          >
                            {slot}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Details form */}
            <div className="lg:col-span-5">
              <form onSubmit={submit} data-testid="booking-form" className="glass rounded-3xl p-6 md:p-8">
                <h3 className="font-display text-xl font-bold tracking-tight text-white">Your details</h3>

                <div className="mt-6 grid gap-4">
                  <input data-testid="booking-name" value={form.name} onChange={update("name")} placeholder="Full name *" className="input-dark" />
                  <input data-testid="booking-email" type="email" value={form.email} onChange={update("email")} placeholder="Email address *" className="input-dark" />
                  <input data-testid="booking-whatsapp" value={form.whatsapp} onChange={update("whatsapp")} placeholder="WhatsApp number *" className="input-dark" />
                  <select data-testid="booking-service" value={form.service} onChange={update("service")} className="input-dark">
                    <option value="">Service you’re interested in *</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                  </select>

                  <div>
                    <span className="mb-2 block font-mono text-xs uppercase tracking-[0.15em] text-white/75">Meeting type</span>
                    <div className="grid grid-cols-2 gap-3">
                      {MEETING_TYPES.map((m) => {
                        const Icon = m.icon;
                        return (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => setMeetingType(m.id)}
                            data-testid={`meeting-${m.id.replace(/\s/g, "")}`}
                            className={`flex flex-col items-start gap-1 rounded-xl border p-4 text-left transition-colors ${
                              meetingType === m.id
                                ? "border-white bg-white/10"
                                : "border-white/10 hover:border-white/25"
                            }`}
                          >
                            <Icon size={18} className="text-white" />
                            <span className="text-sm font-semibold text-white">{m.label}</span>
                            <span className="text-xs text-white/65">{m.desc}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <textarea data-testid="booking-notes" value={form.notes} onChange={update("notes")} rows={3} placeholder="Anything we should know? (optional)" className="input-dark resize-none" />

                  {date && time && (
                    <div className="rounded-xl border border-white/40 bg-white/10 px-4 py-3 text-sm text-white/85" data-testid="booking-summary">
                      <span className="text-white">Selected:</span> {format(date, "EEE, dd MMM yyyy")} at {time}
                    </div>
                  )}

                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileTap={{ scale: 0.97 }}
                    data-testid="booking-submit"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-4 font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-60"
                  >
                    {loading ? "Confirming..." : "Confirm Appointment"}
                    {!loading && <ArrowRight size={18} />}
                  </motion.button>
                </div>
              </form>
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

const Confirmation = ({ done, onReset }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    data-testid="booking-confirmation"
    className="mx-auto max-w-2xl glass rounded-3xl p-10 text-center "
  >
    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/15">
      <CheckCircle2 size={36} className="text-white" />
    </div>
    <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tighter text-white md:text-4xl">
      You’re booked in!
    </h2>
    <p className="mt-3 text-white/70">
      Thanks {done.name.split(" ")[0]} — we’ve received your request. A strategist will
      confirm your <span className="text-white">{done.meeting_type}</span> shortly.
    </p>
    <div className="mt-8 space-y-3 text-left">
      <Row label="Date" value={done.date} />
      <Row label="Time" value={done.time} />
      <Row label="Service" value={done.service} />
      <Row label="Meeting" value={done.meeting_type} />
    </div>
    <button
      onClick={onReset}
      data-testid="booking-another"
      className="mt-8 rounded-full border border-white/20 px-6 py-3 font-semibold text-white transition-colors hover:bg-white/5"
    >
      Book another
    </button>
  </motion.div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between rounded-xl border border-white/10 px-4 py-3">
    <span className="font-mono text-xs uppercase tracking-[0.15em] text-white/65">{label}</span>
    <span className="font-medium text-white">{value}</span>
  </div>
);
